/**
 * URL constants for external links
 * Base URL is retrieved from ThemeContext (premiumSourceUrl from URL params)
 * Falls back to environment variable for local development
 */

// Fallback for local development/testing
const DEFAULT_BASE_URL = import.meta.env.VITE_BASE_URL || 'https://www.hollywoodbets.net';

/**
 * Get the base URL from premiumSourceUrl with fallback for development
 * @param premiumSourceUrl - The source URL from ThemeContext
 * @returns The base URL to use for links
 */
export function getBaseUrl(premiumSourceUrl: string | null | undefined): string {
  // Use premiumSourceUrl if available (production/iframe mode)
  if (premiumSourceUrl) {
    return premiumSourceUrl;
  }
  
  // Fall back to environment variable for local development
  return DEFAULT_BASE_URL;
}

/**
 * Terms and Conditions URL paths by withdrawal method
 */
const TERMS_PATHS = {
  // EFT T's and C's
  EFT: '/content/info-help/terms-and-conditions?contentName=general-terms-and-conditions',
  
  // Cash Send T's and C's
  CASHSEND: '/content/info-help?contentName=cash-send-terms-and-conditions',
  'CASH SEND': '/content/info-help?contentName=cash-send-terms-and-conditions',
  'ABSA CASH SEND': '/content/info-help?contentName=cash-send-terms-and-conditions',
  
  // Instant Money T's and C's
  INSTANTMONEY: '/content/info-help?contentName=cash-send-terms-and-conditions',
  'INSTANT MONEY': '/content/info-help?contentName=cash-send-terms-and-conditions',
  
  // Send-iMali/MobiMoney T's and C's
  'SEND-IMALI/MOBIMONEY': '/content/info-help?contentName=cash-send-terms-and-conditions',
  'SEND-IMALI': '/content/info-help?contentName=cash-send-terms-and-conditions',
  'MOBIMONEY': '/content/info-help?contentName=cash-send-terms-and-conditions',
  'NEDBANK IMALI': '/content/info-help?contentName=cash-send-terms-and-conditions',
  SENDIMALIMOBIMONEY: '/content/info-help?contentName=cash-send-terms-and-conditions',
  
  // eWallet T's and C's
  EWALLET: '/content/info-help?contentName=cash-send-terms-and-conditions',
  'E-WALLET': '/content/info-help?contentName=cash-send-terms-and-conditions',
  
  // Voucher T's and C's
  'MR D': '/content/info-help?contentName=mr-d-voucher-terms-and-conditions',
  'MR D VOUCHER': '/content/info-help?contentName=mr-d-voucher-terms-and-conditions',
  MRD: '/content/info-help?contentName=mr-d-voucher-terms-and-conditions',
  
  TAKEALOT: '/content/info-help?contentName=takealot-voucher-terms-and-conditions',
  'TAKEALOT VOUCHER': '/content/info-help?contentName=takealot-voucher-terms-and-conditions',
  
  'EEZI CASH': '/content/info-help?contentName=eezi-cash-voucher-terms-and-conditions',
  'EEZI CASH VOUCHER': '/content/info-help?contentName=eezi-cash-voucher-terms-and-conditions',
  EEZI: '/content/info-help?contentName=eezi-cash-voucher-terms-and-conditions',
  
  '1VOUCHER': '/content/info-help?contentName=1voucher-terms-and-conditions',
  '1VOUCHER PIN': '/content/info-help?contentName=1voucher-terms-and-conditions',
  
  '1FORYOU': '/content/info-help?contentName=1foryou-app-terms-and-conditions',
  '1VOUCHER APP': '/content/info-help?contentName=1foryou-app-terms-and-conditions',
  
  'SPOT MONEY': '/content/info-help?contentName=spot-money-terms-and-conditions',
  SPOTMONEY: '/content/info-help?contentName=spot-money-terms-and-conditions',
  
  SHOPRITE: '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  SHOPRITEMMA: '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  'SHOPRITE MMA': '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  'SHOPRITE MONEY MARKET': '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  'SHOPRITE MMA ACCOUNT': '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  'SHOPRITE CASH VOUCHER': '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  'SHOPRITE CASH PIN': '/content/info-help?contentName=shoprite-withdrawal-terms-and-conditions',
  
  MOYAPAY: '/content/info-help?contentName=1voucher-terms-and-conditions',
  'MOYA PAY': '/content/info-help?contentName=1voucher-terms-and-conditions',
  
  MTNMOMO: '/content/info-help?contentName=mtn-momo-terms-and-conditions',
  'MTN MOMO': '/content/info-help?contentName=mtn-momo-terms-and-conditions',
  
  'OTT VOUCHER': '/content/info-help?contentName=1voucher-terms-and-conditions',
  OTT: '/content/info-help?contentName=1voucher-terms-and-conditions',
  
  'BLU VOUCHER': '/content/info-help?contentName=1voucher-terms-and-conditions',
  BLU: '/content/info-help?contentName=1voucher-terms-and-conditions',
  
  // Default (1Voucher) T's and C's
  DEFAULT: '/content/info-help?contentName=1voucher-terms-and-conditions',
} as const;

/**
 * Other informational URL paths
 */
const INFO_PATHS = {
  // Cash Send retailers
  CASHSEND_RETAILERS: '/content/info-help?contentName=cash-send-terms-and-conditions',
} as const;

/**
 * Get terms and conditions URL for a specific withdrawal method
 * @param methodName - The withdrawal method name (e.g., 'EFT', 'Cash Send')
 * @param premiumSourceUrl - The source URL from ThemeContext
 * @returns The appropriate terms and conditions URL
 */
export function getTermsUrl(methodName: string | undefined, premiumSourceUrl: string | null | undefined): string {
  const baseUrl = getBaseUrl(premiumSourceUrl);
  
  if (!methodName) {
    return `${baseUrl}${TERMS_PATHS.DEFAULT}`;
  }
  
  const normalizedMethod = methodName.toUpperCase().trim();
  
  // Check for exact matches first
  if (normalizedMethod in TERMS_PATHS) {
    const path = TERMS_PATHS[normalizedMethod as keyof typeof TERMS_PATHS];
    return `${baseUrl}${path}`;
  }
  
  // Check for partial matches
  if (normalizedMethod.includes('CASH SEND') || normalizedMethod.includes('CASHSEND')) {
    return `${baseUrl}${TERMS_PATHS.CASHSEND}`;
  }
  
  if (normalizedMethod.includes('INSTANT MONEY') || normalizedMethod.includes('INSTANTMONEY')) {
    return `${baseUrl}${TERMS_PATHS.INSTANTMONEY}`;
  }
  
  if (normalizedMethod.includes('SEND-IMALI') || normalizedMethod.includes('MOBIMONEY') || 
      normalizedMethod.includes('NEDBANK IMALI') || normalizedMethod.includes('SENDIMALIMOBIMONEY')) {
    return `${baseUrl}${TERMS_PATHS['SEND-IMALI/MOBIMONEY']}`;
  }
  
  if (normalizedMethod.includes('EWALLET') || normalizedMethod.includes('E-WALLET')) {
    return `${baseUrl}${TERMS_PATHS.EWALLET}`;
  }
  
  if (normalizedMethod.includes('MR D') || normalizedMethod.includes('MRD')) {
    return `${baseUrl}${TERMS_PATHS['MR D']}`;
  }
  
  if (normalizedMethod.includes('TAKEALOT')) {
    return `${baseUrl}${TERMS_PATHS.TAKEALOT}`;
  }
  
  if (normalizedMethod.includes('EEZI')) {
    return `${baseUrl}${TERMS_PATHS.EEZI}`;
  }
  
  if (normalizedMethod.includes('1FORYOU') || normalizedMethod.includes('1VOUCHER APP')) {
    return `${baseUrl}${TERMS_PATHS['1FORYOU']}`;
  }
  
  if (normalizedMethod.includes('SPOT MONEY') || normalizedMethod.includes('SPOTMONEY')) {
    return `${baseUrl}${TERMS_PATHS['SPOT MONEY']}`;
  }
  
  if (normalizedMethod.includes('SHOPRITE')) {
    return `${baseUrl}${TERMS_PATHS.SHOPRITE}`;
  }
  
  if (normalizedMethod === 'EFT') {
    return `${baseUrl}${TERMS_PATHS.EFT}`;
  }
  
  // Default fallback
  return `${baseUrl}${TERMS_PATHS.DEFAULT}`;
}

/**
 * Get Cash Send retailers information URL
 * @param premiumSourceUrl - The source URL from ThemeContext
 * @returns The Cash Send retailers URL
 */
export function getCashSendRetailersUrl(premiumSourceUrl: string | null | undefined): string {
  const baseUrl = getBaseUrl(premiumSourceUrl);
  return `${baseUrl}${INFO_PATHS.CASHSEND_RETAILERS}`;
}

/**
 * Get a custom path URL using the base URL
 * @param path - The path to append to the base URL (should start with /)
 * @param premiumSourceUrl - The source URL from ThemeContext
 * @returns The full URL
 */
export function getCustomUrl(path: string, premiumSourceUrl: string | null | undefined): string {
  const baseUrl = getBaseUrl(premiumSourceUrl);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Get banking details page URL
 * @param premiumSourceUrl - The source URL from ThemeContext
 * @returns The banking details URL
 */
export function getBankingDetailsUrl(premiumSourceUrl: string | null | undefined): string {
  const baseUrl = getBaseUrl(premiumSourceUrl);
  return `${baseUrl}/account/my-profile/my-banking-details`;
}
